import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useIsVisible } from "../../Base/Hooks/IsVisible";
import { FormattedMessage, injectIntl } from "react-intl";
import { useMultiDeviceClockState } from "./MultiDeviceClockState";
import MultiDeviceClockButtons from "./MultiDeviceClockButtons/MultiDeviceClockButtons";

import { useInterval } from "../../Base/Hooks/Interval";
import { resetCountDownClock, sendClockEvent, sendCountdownClockEvent } from "../../Base/Utilities/ClockEvents";
import { createBaseEvent } from "../../Base/Utilities/CreateBaseEvent";
import { convertClock, convertDisplayTimeToDataTimeWithMs } from "../../Base/Utilities/ConvertClock";
import { differenceInSeconds } from "../../Base/Utilities/Date";
import { sendEvent } from "../../Base/Utilities/SendEvent";
import { FixtureDetailsContext } from "../../../Config/FixtureDetails";
import ClockAdjustSegment from "./ClockAdjustSegment/ClockAdjustSegment";
import MessagePopup from "../../Base/MessagePopup";
import { generatePeriodTitle } from "../../Base/Utilities/Game.js";
import { useDoActions } from "../../Base/Hooks/doActions.js";
import EndMatchModal from "../EndMatchModal/EndMatchModal.jsx";
import { getPeriodLength } from "../../Base/Utilities/ClockPeriod.js";
import { isShootOutInProgress } from "../../Base/Utilities/MatchProfile.js";

import "./MultiDeviceClock.scss";

const MultiDeviceClock = (props) => {
  const { currentState, updateState, panel, intl, eventStore } = props;
  const { clock, clockCatchup, period, periodCatchup } = currentState;
  const [isVisible, isEnabled] = useIsVisible(panel, props);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [currentMilliseconds, setCurrentMilliseconds] = useState(0);
  const [clockRunning, setClockRunning] = useState(false);
  const [editClock, setEditClock] = useState(false);
  const [pressedKey, setPressedKey] = useState(null);
  const [clockState] = useMultiDeviceClockState(props, clockRunning);
  const [confirmationPopup, setConfirmationPopup] = useState(null);
  const [doAction, setDoAction] = useState(false);
  const [actionsDone] = useDoActions(panel, props, doAction);
  const [catchupState, setCatchupState] = useState({
    startedFromCatchup: false,
    stoppedFromCatchup: false,
  });
  const [tickStart, setTickStart] = useState(performance.now());
  const [elapsedMs, setElapsedMs] = useState(0);
  const { fixtureProfile, sport } = useContext(FixtureDetailsContext);
  const { formatMessage } = intl;

  const getDisplayTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds - mins * 60;

    return mins.toString().padStart(2, 0) + ":" + secs.toString().padStart(2, 0);
  };

  const getMsDisplay = () => {
    return `.${currentMilliseconds.toString().padStart(3, 0)}`;
  };

  const isTimeDisplayVisible = () => {
    return !["waitingForExtraPeriodStart", "shootOutInProgress", "waitingForFixtureEnd", "fixtureComplete"].includes(
      clockState,
    );
  };

  const isMillisecondsVisible = () => {
    if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
      return totalSeconds < 60;
    } else {
      return totalSeconds > getPeriodLength(period.periodId, fixtureProfile) * 60 - 60;
    }
  };

  const isTimeEditButtonVisible = () => {
    return ["running", "stopped", "waitingForPeriodEnd"].includes(clockState);
  };

  const localSetClockRunning = (running) => {
    setClockRunning(running);
    if (running) {
      setCatchupState((state) => ({
        ...state,
        startedFromCatchup: false,
      }));
      setTickStart(performance.now());
    } else {
      setCatchupState((state) => ({
        ...state,
        stoppedFromCatchup: false,
      }));
    }
  };

  const catchupSetClockRunning = (running) => {
    setClockRunning(running);
    if (running) {
      setCatchupState((state) => ({
        ...state,
        startedFromCatchup: true,
      }));
      setTickStart(performance.now());
    } else {
      setCatchupState((state) => ({
        ...state,
        stoppedFromCatchup: true,
      }));
    }
  };

  const getPeriodStartTime = () => {
    if (!period) {
      return 0;
    }

    if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
      return getPeriodLength(period.periodId, fixtureProfile) * 60;
    }
    return 0;
  };

  const getPeriodEndTime = () => {
    if (!period) {
      return 0;
    }

    if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
      return 0;
    }

    return getPeriodLength(period.periodId, fixtureProfile) * 60;
  };

  const clampPeriodTimeStartEnd = (seconds) => {
    let newSeconds;
    if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
      newSeconds = Math.max(getPeriodEndTime(), Math.min(seconds, getPeriodStartTime()));
    } else {
      newSeconds = Math.max(getPeriodStartTime(), Math.min(seconds, getPeriodEndTime()));
    }
    return newSeconds;
  };

  const calculatePeriodTime = () => {
    if (sport === "handball") {
      return period.periodId >= fixtureProfile.initialExtraPeriodId || period.periodId === 1
        ? totalSeconds
        : totalSeconds + fixtureProfile.periodLength * 60;
    }
    return totalSeconds;
  };

  const resetClockToStart = () => {
    const startTime = getPeriodStartTime();
    setTotalSeconds(startTime);
    updateState("clock", {
      displayTime: getDisplayTime(startTime),
      clockRunning: currentState?.clock?.clockRunning,
      clockStatus: currentState?.clock?.clockStatus,
    });
  };

  const updateConfirmationPopup = (text, onConfirm) => {
    setConfirmationPopup(
      <MessagePopup
        text={text}
        onConfirm={() => {
          onConfirm();
          setConfirmationPopup(null);
        }}
        onDecline={() => setConfirmationPopup(null)}
      />,
    );
  };

  const goToNextPeriod = () => {
    sendClockEvent("sport", "period", "end", props);
    updateState("period", {
      periodId: period.periodId,
      periodStatus: "periodEnd",
    });
  };

  const confirmPeriod = () => {
    sendClockEvent("sport", "period", "confirmed", props);
    if (period.periodId >= fixtureProfile.numberOfPeriods) {
      updateState("period", {
        periodId: period.periodId,
        periodStatus: "pendingExtra",
      });
    } else {
      const newPeriodId = period.periodId + 1;
      updateState("period", {
        periodId: newPeriodId,
        periodStatus: "pending",
      });
      sendClockEvent("sport", "period", "pending", props);
    }
    resetClockToStart();
  };

  const goToNextExtraPeriod = () => {
    let newPeriod =
      period.periodId === fixtureProfile.numberOfPeriods ? fixtureProfile.initialExtraPeriodId : period.periodId + 1;
    updateState("period", {
      periodId: newPeriod,
      periodStatus: "pending",
    });
    sendClockEvent("sport", "period", "pending", props);
  };

  const sendClockAdjustment = (seconds) => {
    let newTime = ("PT" + getDisplayTime(seconds) + "S").replace(":", "M");
    sendClockEvent("clock", "main", "adjust", props, "value", newTime);
  };

  const goToShootOutPeriod = () => {
    updateState("period", {
      ...period,
      periodId: fixtureProfile.shootOutPeriodId,
    });
    sendClockEvent("sport", "period", "pending", props);
    sendClockEvent("sport", "period", "start", props);
  };

  const isShootoutPendingEventPresent = () => {
    return !!currentState?.mqtt?.plays.find((event) => event.eventType === "shootOut" && event.subType === "pending");
  };

  const sendShootoutPending = () => {
    const action = createBaseEvent(currentState);

    action.event.eventType = "shootOut";
    action.event.subType = "pending";

    sendEvent(action, props);

    if (sport === "handball") {
      sendClockEvent("internal", "betStop", null, props);
    }
  };

  const sendFixtureEvent = (subType) => {
    const action = createBaseEvent(currentState);
    action.event.eventType = "fixture";
    action.event.subType = subType;

    sendEvent(action, props);
  };

  const sendVideoReviewEvent = (subType) => {
    if (sport === "handball") {
      const lastVideoReviewEvent = eventStore.findLast(
        (event) => event.class === "sport" && event.eventType === "videoReview",
      );
      if (subType === "end" && lastVideoReviewEvent?.subType === "start") {
        const action = createBaseEvent(currentState);
        action.event.eventType = "videoReview";
        action.event.subType = subType;
        action.event.clock = convertDisplayTimeToDataTimeWithMs(
          currentState?.clock?.displayTime,
          currentState?.clockMs,
        );

        sendEvent(action, props);
      }
    }
  };

  const displayTimeToInt = (displayTime) => {
    return displayTime.split(":").map((v) => parseInt(v, 10));
  };

  const clockTick = useCallback(() => {
    if (clockRunning) {
      const elapsed = performance.now() - tickStart;
      setTickStart(performance.now());
      setElapsedMs((curr) => elapsed + curr);
    }
  }, [clockRunning, tickStart]);

  useEffect(() => {
    updateState("clockMs", currentMilliseconds);
  }, [currentMilliseconds]);

  useEffect(() => {
    if (
      clockRunning &&
      (totalSeconds !== getPeriodEndTime() || (totalSeconds === getPeriodEndTime() && currentMilliseconds !== 0))
    ) {
      if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
        const newMs = Math.max(0, Math.min(999, 1000 - Math.floor(elapsedMs)));
        setCurrentMilliseconds(newMs);
      } else {
        const newMs = totalSeconds === getPeriodEndTime() ? 0 : Math.max(0, Math.min(999, Math.floor(elapsedMs)));
        setCurrentMilliseconds(newMs);
      }
    }

    if (elapsedMs >= 1000) {
      if (totalSeconds === getPeriodEndTime()) {
        setElapsedMs(0);
      } else {
        const elapsedSeconds = Math.floor(elapsedMs / 1000);
        setElapsedMs((curr) => curr - elapsedSeconds * 1000);
        if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
          setTotalSeconds((prevSec) => Math.max(prevSec - elapsedSeconds, getPeriodEndTime()));
          setCurrentMilliseconds(999);
        } else {
          setTotalSeconds((prevSec) => Math.min(prevSec + elapsedSeconds, getPeriodEndTime()));
          setCurrentMilliseconds(1);
        }
      }
    }
  }, [elapsedMs, totalSeconds, period, fixtureProfile?.clockDirection]);

  const handleTimeEdit = (seconds) => {
    let newSeconds = clampPeriodTimeStartEnd(totalSeconds + seconds);
    setTotalSeconds(newSeconds);
    sendClockAdjustment(newSeconds);
  };

  const handleKeyDown = (event) => {
    if (event.code) {
      setPressedKey(event.code);
    }
  };

  useEffect(() => {
    if (actionsDone) {
      setDoAction(false);
    }
  }, [actionsDone]);

  useEffect(() => {
    if (!clock?.displayTime) {
      resetClockToStart();
      return;
    }

    if (clock.catchupStartEvent) {
      const deltaTime = Math.round(
        differenceInSeconds(
          new Date(),
          new Date(`${clock.catchupStartEvent.eventTime}${clock.catchupStartEvent.eventTime.includes("Z") ? "" : "Z"}`),
        ),
      );
      const splitTime = convertClock(clock.catchupStartEvent.clock).split(":");
      let allSeconds;
      if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
        allSeconds = parseInt(splitTime[0], 10) * 60 + parseInt(splitTime[1], 10) - deltaTime;
      } else {
        allSeconds = parseInt(splitTime[0], 10) * 60 + parseInt(splitTime[1], 10) + deltaTime;
      }
      setTotalSeconds(clampPeriodTimeStartEnd(allSeconds));
    } else {
      const splitTime = clock.displayTime.split(":");
      const allSeconds = parseInt(splitTime[0], 10) * 60 + parseInt(splitTime[1], 10);
      setTotalSeconds(clampPeriodTimeStartEnd(allSeconds));
    }
    catchupSetClockRunning(clock.clockRunning);
  }, []);

  useEffect(() => {
    if (period?.periodStatus === "initial" && sport !== "hockey") {
      if (period.periodId === 1) {
        sendClockEvent("sport", "fixture", "pending", props);
      }
      sendClockEvent("sport", "period", "pending", props);
      updateState("period", {
        periodId: currentState.period.periodId,
        periodStatus: "pending",
      });
    }
    // eslint-disable-next-line
  }, [period]);

  useEffect(() => {
    if (clock?.clockStatus === "auto") {
      localSetClockRunning(clock.clockRunning);
    }
  }, [clock?.clockStatus, clock?.clockRunning]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (pressedKey && ["running", "stopped", "waitingForPeriodEnd", "waitingForPeriodStart"].includes(clockState)) {
      switch (pressedKey) {
        case "Space":
          if (["running", "stopped"].includes(clockState)) {
            localSetClockRunning(!clockRunning);
          }
          if (["waitingForPeriodStart"].includes(clockState)) {
            buttonEventHandlers.startPeriodButtonPressed()
          }
          break;
        case "ArrowUp":
          if (!clockRunning && editClock) {
            handleTimeEdit(1);
          }
          break;
        case "ArrowDown":
          if (!clockRunning && editClock) {
            handleTimeEdit(-1);
          }
          break;
        case "ArrowRight":
          if (!clockRunning && editClock) {
            handleTimeEdit(60);
          }
          break;
        case "ArrowLeft":
          if (!clockRunning && editClock) {
            handleTimeEdit(-60);
          }
          break;
        case "KeyC":
          updateState("region", null);
          break;
        default:
          break;
      }
    }
    setPressedKey(null);
  }, [pressedKey]);

  useEffect(() => {
    if (!clockCatchup || !clockCatchup.displayTime) {
      return;
    }

    const [mins, secs] = displayTimeToInt(clockCatchup.displayTime);
    const newTotalSeconds = clampPeriodTimeStartEnd(mins * 60 + secs);
    setTotalSeconds(newTotalSeconds);
    if (clockCatchup.msCatchup != null) {
      if (fixtureProfile?.clockDirection === "COUNT_DOWN") {
        setElapsedMs(Math.max(0, Math.min(999, 1000 - clockCatchup.msCatchup)));
      } else {
        setElapsedMs(Math.max(0, Math.min(999, clockCatchup.msCatchup)));
      }
      setCurrentMilliseconds(clockCatchup.msCatchup);
    }
    catchupSetClockRunning(clockCatchup.clockRunning);
  }, [clockCatchup]);

  useEffect(() => {
    return () => {
      updateState("clockCatchup", null);
      updateState("periodCatchup", null);
    };
  }, []);

  useEffect(() => {
    if (periodCatchup) {
      updateState("period", {
        periodId: periodCatchup.periodId ?? 1,
        periodStatus: periodCatchup.periodStatus,
      });
    }
  }, [periodCatchup]);

  useEffect(() => {
    if (
      [
        "running",
        "stopped",
        "waitingForStartMatch",
        "waitingForPeriodEnd",
        "waitingForPeriodStart",
        "waitingForExtraPeriodStart",
      ].includes(clockState)
    ) {
      if (clockRunning && !catchupState.startedFromCatchup) {
        sendClockEvent("clock", "main", "start", props);
      } else if (!clockRunning && !catchupState.stoppedFromCatchup) {
        sendClockEvent("clock", "main", "stop", props);
      }
    }
  }, [clockRunning]);

  useEffect(() => {
    updateState("clock", {
      displayTime: getDisplayTime(totalSeconds),
      clockRunning: clockRunning,
      clockStatus: clockState,
    });
  }, [totalSeconds, clockRunning, clockState]);

  useEffect(() => {
    if (!actionsDone) {
      setDoAction(true);
    }
  }, [clock?.clockStatus]);

  useEffect(() => {
    if (["running", "stopped", "waitingForPeriodEnd"].includes(clockState) && period.periodStatus !== "periodEnd") {
      if (totalSeconds === getPeriodEndTime() && currentMilliseconds === 0) {
        updateState("period", {
          periodId: period?.periodId ?? 1,
          periodStatus: "ended",
        });
        localSetClockRunning(false);
      } else {
        if (period.periodStatus !== "inProgress") {
          updateState("period", {
            periodId: period?.periodId ?? 1,
            periodStatus: "inProgress",
          });
        }
      }
    } else if (clockState === "periodStarted") {
      updateState("period", {
        periodId: period.periodId,
        periodStatus: "inProgress",
      });
    }
  }, [totalSeconds, currentMilliseconds, clockState]);

  useEffect(() => {
    if (clockState === "undefined") {
      localSetClockRunning(false);
      if (["ended", "periodEnd", "fixtureConfirm", "fixtureComplete"].includes(period.periodStatus)) {
        setTotalSeconds(getPeriodEndTime());
      } else {
        setTotalSeconds(getPeriodStartTime());
      }
    }
  }, [clockState, period.periodStatus]);

  useEffect(() => {
    if (["pending", "pendingExtra"].includes(period.periodStatus)) {
      resetClockToStart();
    }
  }, [period.periodStatus]);

  const buttonEventHandlers = useMemo(() => {
    return {
      startButtonPressed: () => {
        localSetClockRunning(true);
        sendVideoReviewEvent("end");
        if (sport === "hockey") {
          sendCountdownClockEvent(props, "stop");
        }
      },
      stopButtonPressed: () => {
        localSetClockRunning(false);
      },
      onPitchButtonPressed: () => {
        sendFixtureEvent("on_pitch");
      },
      endOnPitchButtonPressed: () => {
        updateState("onPitchEnd", true);
      },
      warmUpButtonPressed: () => {
        updateState("primary", "warmup-clock");
      },
      warmingUpButtonPressed: () => {
        sendFixtureEvent("warm_up");
        updateState("onPitchEnd", false);
      },
      endWarmingUpButtonPressed: () => {
        sendFixtureEvent("about_to_start");
      },
      throwOffButtonPressed: () => {
        updateState("primary", "throw-off");
      },
      centrePassButtonPressed: () => {
        updateState("primary", "centre-pass");
      },
      startMatchButtonPressed: () => {
        sendClockEvent("sport", "fixture", "start", props);
        sendClockEvent("sport", "period", "start", props);
        updateState("period", {
          periodId: period.periodId,
          periodStatus: "inProgress",
        });
        localSetClockRunning(true);
      },
      endPeriodButtonPressed: () => {
        updateConfirmationPopup(
          formatMessage({
            id: "clock.continue.period.confirmationMessage",
            defaultMessage: "End Period? Are you sure?",
          }),
          goToNextPeriod,
        );
      },
      confirmPeriodButtonPressed: () => {
        confirmPeriod();
      },
      startPeriodButtonPressed: () => {
        updateState("period", {
          periodId: period.periodId,
          periodStatus: "periodStarted",
        });
        localSetClockRunning(true);
        sendClockEvent("sport", "period", "start", props);
        if (sport === "hockey" && period.periodId > 1 && period.periodId <= fixtureProfile.numberOfPeriods) {
          resetCountDownClock(updateState);
        }
      },
      nextExtraPeriodButtonPressed: () => {
        updateConfirmationPopup(
          formatMessage({
            id: "clock.continue.period.confirmationMessage",
            defaultMessage: "End Period? Are you sure?",
          }),
          goToNextExtraPeriod,
        );
      },
      shootOutButtonPressed: () => {
        const showModal = () => {
          if (currentState.userMode === "clockOperator") {
            updateState("primary", "shootOut-clock");
          } else {
            updateState("primary", "shootOut");
          }
        };

        if (!isShootOutInProgress(currentState, fixtureProfile)) {
          updateConfirmationPopup(
            formatMessage({
              id: "clock.continue.period.confirmationMessage",
              defaultMessage: "End Period? Are you sure?",
            }),
            () => {
              goToShootOutPeriod();
              if (!isShootoutPendingEventPresent()) {
                sendShootoutPending();
              }
              showModal();
            },
          );
        } else {
          showModal();
        }
      },
      endMatchButtonPressed: () => {
        setConfirmationPopup(<EndMatchModal {...props} setShowModal={setConfirmationPopup} />);
      },
      confirmFixtureButtonPressed: () => {
        sendClockEvent("sport", "fixture", "confirmed", props);

        if (sport === "handball") {
          sendClockEvent("internal", "betStop", null, props);
        }

        updateState("period", {
          periodId: period.periodId,
          periodStatus: "fixtureComplete",
        });
      },
      endOvertimePressed: () => {
        updateConfirmationPopup(
          formatMessage({
            id: "clock.end.overtime.confirmationMessage",
            defaultMessage: "End Overtime? Are you sure?",
          }),
          goToNextPeriod,
        );
      },
    };
  }, [period, currentState, fixtureProfile]);

  useInterval(clockTick, 10);

  const useReversedButtonColors = sport === "handball";

  return (
    <>
      {isVisible && (
        <div className={panel.layout + " enabled-" + isEnabled}>
          {confirmationPopup}
          <div className="mdclock-period">
            {generatePeriodTitle(sport, period.periodId, fixtureProfile, formatMessage, true)}
          </div>
          <div className="mdclock-inner">
            {isTimeEditButtonVisible() && (
              <div className="mdclock-edit">
                {!editClock && <i className="edit-start fas fa-pen-square" onClick={() => setEditClock(true)}></i>}
                {editClock && <i className="edit-done fas fa-check-square" onClick={() => setEditClock(false)}></i>}
              </div>
            )}
            {isTimeEditButtonVisible() && editClock && (
              <div className="mdclock-adjust-segment-left">
                <ClockAdjustSegment onUpPressed={() => handleTimeEdit(60)} onDownPressed={() => handleTimeEdit(-60)} />
              </div>
            )}
            {isTimeEditButtonVisible() && editClock && (
              <div className="mdclock-adjust-segment-right">
                <ClockAdjustSegment onUpPressed={() => handleTimeEdit(1)} onDownPressed={() => handleTimeEdit(-1)} />
              </div>
            )}
            <div className="mdclock-time-buttons">
              {isTimeDisplayVisible() && (
                <div className="mdclock-time-container">
                  <div
                    className="mdclock-time"
                    style={
                      isMillisecondsVisible()
                        ? {
                            fontSize: "2rem",
                          }
                        : {}
                    }
                  >
                    {getDisplayTime(calculatePeriodTime())}
                  </div>
                  {isMillisecondsVisible() && <span>{getMsDisplay()}</span>}
                </div>
              )}
              <div
                className={`mdclock-buttons ${useReversedButtonColors ? "mdclock-buttons-reversed" : ""}`}
                style={isTimeDisplayVisible() ? { width: "50%" } : { width: "100%" }}
              >
                <MultiDeviceClockButtons {...props} {...buttonEventHandlers} clockState={clockState} />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default injectIntl(MultiDeviceClock);
